import { DayPilot } from 'daypilot-pro-angular';

import {
  ApiDateTime,
  CalendarDay,
  addCalendarDays,
  calendarDayOfWeek,
  calendarDayParts,
  startOfIsoCalendarWeek,
  timeOfDayFromApi,
  toCalendarDay,
} from '@shared/models/dateTypes';

/**
 * The school timetable (skolekalender, #1138): the day with the faciliteter as columns, or the week
 * with the days as columns and the faciliteter under each day. Pure layout, kept apart from the
 * DayPilot component so it can be tested without a DOM.
 */
export type SchoolCalendarView = 'day' | 'week';

export interface SchoolCalendarResource {
  id: number;
  name: string;
}

/** The short Danish weekday names, Sunday first (as `calendarDayOfWeek` counts). */
export const WEEKDAYS_SHORT = ['søn', 'man', 'tir', 'ons', 'tor', 'fre', 'lør'];

/** The calendar's hours unless something lies outside them: an ordinary school day and evening. */
export const DEFAULT_HOURS = { begins: 8, ends: 22 } as const;

/** The days the view shows: the day itself, or Monday to Sunday of its week. */
export function viewDays(view: SchoolCalendarView, day: CalendarDay): CalendarDay[] {
  if (view === 'day') return [day];
  const monday = startOfIsoCalendarWeek(day);
  return Array.from({ length: 7 }, (_, i) => addCalendarDays(monday, i));
}

/** "man 19/10" */
export function shortDayLabel(day: CalendarDay): string {
  const { month, date } = calendarDayParts(day);
  return `${WEEKDAYS_SHORT[calendarDayOfWeek(day)]} ${date}/${month}`;
}

/**
 * A leaf column's id: the facilitet on that day. DayPilot puts an event in the first column whose id
 * matches its `resource`, so the week's seven columns of one facilitet need ids of their own — the
 * facilitet id alone would pile the whole week into one of them.
 */
export function columnId(resourceId: number, day: CalendarDay): string {
  return `${resourceId}@${day}`;
}

/**
 * DayPilot's columns. The day: one column per facilitet. The week: one column per day, and — with
 * more than one facilitet chosen — the faciliteter as a second header level under each day. Every leaf
 * is a facilitet on a day ({@link columnId}) with that day as `start`.
 */
export function buildColumns(
  view: SchoolCalendarView,
  day: CalendarDay,
  resources: SchoolCalendarResource[],
): DayPilot.CalendarColumnData[] {
  if (view === 'day') {
    return resources.map((r) => ({ id: columnId(r.id, day), name: r.name, start: day }));
  }

  return viewDays('week', day).map((d) =>
    resources.length === 1
      ? { id: columnId(resources[0].id, d), name: shortDayLabel(d), start: d }
      : {
          name: shortDayLabel(d),
          children: resources.map((r) => ({ id: columnId(r.id, d), name: r.name, start: d })),
        },
  );
}

/**
 * The hours the grid spans: 08–22 (design 2a), widened in whole hours when something starts
 * earlier or ends later, so nothing is cut off. A booking running past midnight keeps the grid to
 * the end of the day.
 */
export function hourRange(periods: { start: ApiDateTime; end: ApiDateTime }[]): {
  begins: number;
  ends: number;
} {
  let begins: number = DEFAULT_HOURS.begins;
  let ends: number = DEFAULT_HOURS.ends;
  for (const p of periods) {
    const [startHour] = timeOfDayFromApi(p.start).split(':').map(Number);
    const [endHour, endMinute] = timeOfDayFromApi(p.end).split(':').map(Number);
    const endsNextDay = toCalendarDay(p.end) > toCalendarDay(p.start);
    begins = Math.min(begins, startHour);
    ends = Math.max(ends, endsNextDay ? 24 : endHour + (endMinute > 0 ? 1 : 0));
  }
  return { begins, ends };
}

/**
 * A closed stretch of a day — from Åbningstider — to grey out: in its facilitet's column, or in
 * every column of the day when it has none.
 */
export interface SchoolCalendarClosed {
  day: CalendarDay;
  /** The facilitet it holds for; none = all of them. */
  resourceId?: number;
  /** Minutes after midnight; `to` up to 1440. */
  from: number;
  to: number;
  /** "Lukket", "Lukket · Juledag", "Spærret · …" */
  label: string;
}

/**
 * The grid's hours widened to the opening hours, in whole hours: from the earliest opening to the
 * latest close, so the calendar runs to closing time and the closed part of its last hour is seen.
 * A day closed all day widens nothing.
 */
export function widenToOpeningHours(
  range: { begins: number; ends: number },
  closed: readonly SchoolCalendarClosed[],
): { begins: number; ends: number } {
  let { begins, ends } = range;
  for (const c of closed) {
    if (c.from === 0 && c.to === 24 * 60) continue;
    if (c.from === 0) begins = Math.min(begins, Math.floor(c.to / 60));
    if (c.to === 24 * 60) ends = Math.max(ends, Math.ceil(c.from / 60));
  }
  return { begins, ends };
}

/**
 * The closed stretches by column (`resourceId@day`, as {@link columnId}), built once so each of the
 * grid's cells looks up its own few instead of searching them all. Stretches for every facilitet
 * are under `*@day`.
 */
export function closedByColumn(
  closed: readonly SchoolCalendarClosed[],
): ReadonlyMap<string, SchoolCalendarClosed[]> {
  const byColumn = new Map<string, SchoolCalendarClosed[]>();
  for (const c of closed) {
    const key = `${c.resourceId ?? '*'}@${c.day}`;
    byColumn.set(key, [...(byColumn.get(key) ?? []), c]);
  }
  return byColumn;
}

/** The closed stretch a cell of `column` (`resourceId@day`), starting at `minute`, lies in. */
export function closedAt(
  byColumn: ReadonlyMap<string, SchoolCalendarClosed[]>,
  column: string,
  minute: number,
): SchoolCalendarClosed | undefined {
  const inCell = (c: SchoolCalendarClosed) => minute >= c.from && minute < c.to;
  const day = column.slice(column.indexOf('@') + 1);
  return byColumn.get(column)?.find(inCell) ?? byColumn.get(`*@${day}`)?.find(inCell);
}

/** A part of a Valgfri tid (#1138), in minutes from the block's start. */
export interface FlexSpan {
  from: number;
  to: number;
}

/**
 * The span between two points in a Valgfri tid of `duration` minutes — a drag's two ends, in either
 * order — on the block's steps of `step` minutes, at least one step and `minMinutes` long, within
 * the block. Without steps the block is taken whole.
 */
export function snapFlexSpan(
  duration: number,
  step: number,
  minMinutes: number,
  a: number,
  b: number,
): FlexSpan {
  const unit = step > 0 ? step : duration;
  const low = Math.max(0, Math.min(a, b));
  const high = Math.min(duration, Math.max(a, b));
  let from = Math.min(Math.floor(low / unit) * unit, Math.max(0, duration - unit));
  let to = Math.max(from + unit, Math.ceil(high / unit) * unit);
  const least = Math.max(unit, Math.ceil((minMinutes || 0) / unit) * unit);
  if (to - from < least) to = from + least;
  if (to > duration) {
    to = duration;
    from = Math.max(0, duration - least);
  }
  return { from, to };
}

/** The wall clock `minutes` after `start`, "19:30" — read off the API's own string, no instant. */
export function clockAfter(start: ApiDateTime, minutes: number): string {
  const [h, m] = timeOfDayFromApi(start).split(':').map(Number);
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** A box's length in minutes. */
export const minutesBetween = (start: ApiDateTime, end: ApiDateTime) =>
  (Date.parse(end) - Date.parse(start)) / 60_000;

/** A Valgfri tid's default span: the shortest allowed, from its start. */
export const defaultFlexSpan = (
  start: ApiDateTime,
  end: ApiDateTime,
  step = 0,
  minMinutes = 0,
): FlexSpan => snapFlexSpan(minutesBetween(start, end), step, minMinutes, 0, 0);

/**
 * How long the pointer rests on a time before its detail card shows — passing over shows none, in
 * the skolekalender and on the list alike. Focus from the keyboard shows it at once.
 */
export const HOVER_REST_MS = 400;

/** How long a detail card stays after the pointer leaves its time, so it can travel onto the card. */
export const HOVER_GRACE_MS = 200;

/** A named closing as the skolekalender writes it: "Lukket · skole juleferie" (#1138). */
export const closedText = (label: string) => `Lukket · ${label}`;
